import React, { useState } from 'react'
import { Card, Table, Tag, Space, Empty, Collapse, Alert, Tooltip, Button, message, Checkbox, Modal, Typography } from 'antd'
import FulfillmentStatusLabel from '@shared/fulfillments/FulfillmentStatusLabel'
import FulfillmentSplitModal, { type SplitItemPayload, type PreviewResult } from '@shared/orders/FulfillmentSplitModal'
import FulfillmentConceptModal from '@shared/orders/FulfillmentConceptModal'
import ChangeShippingTypeModal from '@shared/fulfillments/ChangeShippingTypeModal'
import {
  LinkOutlined,
  EnvironmentOutlined,
  PartitionOutlined,
  MergeCellsOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { formatDateTime } from '../../../utils/dateTime'
import useInventoryModal from '@hooks/shared/useInventoryModal'

const { Text } = Typography

interface FulfillmentItem {
  id: string
  order_id?: string
  status?: string
  quantity?: number
  inventories?: Array<Record<string, unknown>>
  product?: { sku?: string; name?: string; [key: string]: unknown }
  unknown_product?: { sku?: string; name?: string; [key: string]: unknown }
  [key: string]: unknown
}

interface SiblingOrder {
  id: string
  name?: string
  placed_at?: string
  [key: string]: unknown
}

interface Shipment {
  deleted_at?: string | null
  source?: string
  [key: string]: unknown
}

interface Warehouse {
  id?: string
  name?: string
  title?: string
  [key: string]: unknown
}

interface Fulfillment {
  id: string
  number?: string
  status?: string
  created_at?: string
  platform_tracking_number?: string
  warehouse?: Warehouse
  items?: FulfillmentItem[]
  sibling_orders?: SiblingOrder[]
  shipments?: Shipment[]
  is_cod?: boolean
  total_amount?: string | number
  currency?: string
  splittable?: boolean
  mergeable?: boolean
  // ChangeShippingTypeModal 需要 + header 顯示物流類型用
  merchant_id?: string
  shipping_type?: { id: string; name?: string; code?: string; source?: string } | null
  [key: string]: unknown
}

interface FulfillmentsPanelProps {
  fulfillments?: Fulfillment[]
  role?: 'admin' | 'operator'
  currentOrderId?: string
  onReload?: () => void
  merchantWarehouses?: Warehouse[]
  currentWarehouse?: Warehouse | null
  attributeLabelByName?: Record<string, string>
}

const FulfillmentsPanel: React.FC<FulfillmentsPanelProps> = ({
  fulfillments = [],
  role = 'admin',
  currentOrderId,
  onReload,
  merchantWarehouses = [],
  currentWarehouse,
  attributeLabelByName,
}) => {
  const { t } = useTranslation()
  const [splitModalVisible, setSplitModalVisible] = useState(false)
  const [targetFulfillment, setTargetFulfillment] = useState<Fulfillment | null>(null)
  const [splitting, setSplitting] = useState(false)
  const [selectedFulfillments, setSelectedFulfillments] = useState<string[]>([])
  const [merging, setMerging] = useState(false)
  const [completingId, setCompletingId] = useState<string | null>(null)
  const [conceptModalVisible, setConceptModalVisible] = useState(false)
  const [changeShippingTypeVisible, setChangeShippingTypeVisible] = useState(false)
  const [changeShippingTypeTarget, setChangeShippingTypeTarget] = useState<Fulfillment | null>(null)
  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  const {
    fulfillmentItemColumns,
    showAllInventories,
    renderInventoryModals,
  } = useInventoryModal({ attributeLabelByName })

  if (!fulfillments || fulfillments.length === 0) {
    return <Empty description={t('shared.orders.detail.fulfillments.no_fulfillments')} />
  }

  const getFulfillmentService = async () => {
    if (role === 'admin') {
      const module = await import('@services/admin/fulfillmentService')
      return module.fulfillmentService
    } else {
      const module = await import('@services/operator/fulfillmentService')
      return module.fulfillmentService
    }
  }

  // itemsPayload 是 [{id, quantity}, ...] — 雖然底下 fulfillmentService.split 把它放進
  // body.fulfillment_item_ids（命名歷史包袱），但 wire shape 一直是物件陣列。
  const handleSplit = async (itemsPayload: SplitItemPayload[], warehouseId: string | null, shippingTypeId: string | null, desiredAmounts: [string, string]) => {
    try {
      setSplitting(true)
      const fulfillmentService = await getFulfillmentService()

      const result = await (fulfillmentService as unknown as { split: (...args: unknown[]) => Promise<{ success?: boolean; message?: string; interpolations?: Record<string, unknown> }> }).split(targetFulfillment!.id, itemsPayload, warehouseId, shippingTypeId, desiredAmounts)
      if (result.success) {
        message.success(t('shared.orders.detail.fulfillments.split.success', { defaultValue: 'Split success' }))
        setSplitModalVisible(false)
        setTargetFulfillment(null)
        if (onReload) onReload()
      } else {
        // backend 回 message = error_code（per render_error 約定）→ FE 從 errors namespace 翻譯
        const code = result.message
        // 缺翻譯時不直接顯示 snake_case error code 給使用者，fallback 成「操作失敗 (code)」
        // — 可讀的通用訊息 + 保留 code 給支援人員 / 工程師排查
        const fallback = code ? `${t('common.error.operation_failed')} (${code})` : t('common.error.operation_failed')
        const translated = code ? t(`errors.${code}`, { defaultValue: fallback, ...(result.interpolations || {}) }) : null
        message.error(translated || fallback)
      }
    } catch (err) {
      console.error('[FulfillmentsPanel] split failed:', err)
      message.error(t('common.error.operation_failed'))
    } finally {
      setSplitting(false)
    }
  }

  const executeMerge = async () => {
    try {
      setMerging(true)
      const fulfillmentService = await getFulfillmentService()

      const result = await (fulfillmentService as { merge: (ids: string[]) => Promise<{ success?: boolean; message?: string; interpolations?: Record<string, unknown> }> }).merge(selectedFulfillments)
      if (result.success) {
        message.success(t('shared.orders.merge.success', { defaultValue: 'Merge Successful' }))
        setSelectedFulfillments([])
        if (onReload) onReload()
      } else {
        const code = result.message
        // 缺翻譯時不直接顯示 snake_case error code 給使用者，fallback 成「操作失敗 (code)」
        // — 可讀的通用訊息 + 保留 code 給支援人員 / 工程師排查
        const fallback = code ? `${t('common.error.operation_failed')} (${code})` : t('common.error.operation_failed')
        const translated = code ? t(`errors.${code}`, { defaultValue: fallback, ...(result.interpolations || {}) }) : null
        message.error(translated || fallback)
      }
    } catch (err) {
      console.error('[FulfillmentsPanel] merge failed:', err)
      message.error(t('common.error.operation_failed'))
    } finally {
      setMerging(false)
    }
  }

  const handleMerge = async () => {
    if (selectedFulfillments.length < 2) {
      message.warning(t('shared.orders.merge.select_at_least_two', { defaultValue: 'Please select at least two orders to merge' }))
      return
    }

    // 偵測是否有 fulfillment 不屬於當前操作倉庫
    if (currentWarehouse?.id) {
      const selectedData = fulfillments.filter(f => selectedFulfillments.includes(f.id))
      const otherWarehouseFulfillments = selectedData.filter(f => f.warehouse?.id && f.warehouse.id !== currentWarehouse.id)

      if (otherWarehouseFulfillments.length > 0) {
        const otherWarehouseNames = [...new Set(otherWarehouseFulfillments.map(f => f.warehouse?.name).filter(Boolean))].join('、')
        Modal.confirm({
          title: t('shared.orders.merge.cross_warehouse_title', { defaultValue: 'Cross-warehouse Merge' }),
          content: t('shared.orders.merge.cross_warehouse_confirm', {
            warehouses: otherWarehouseNames,
            target: currentWarehouse.title || currentWarehouse.name,
            defaultValue: `Some selected fulfillments belong to other warehouses (${otherWarehouseNames}). After merging, all items will be consolidated into the current warehouse (${currentWarehouse.title || currentWarehouse.name}). Continue?`
          }),
          okText: t('common.confirm', { defaultValue: 'Confirm' }),
          cancelText: t('common.cancel', { defaultValue: 'Cancel' }),
          onOk: executeMerge,
        })
        return
      }
    }

    executeMerge()
  }

  const openSplitModal = (fulfillment: Fulfillment) => {
    setTargetFulfillment(fulfillment)
    setSplitModalVisible(true)
  }

  const handleComplete = (fulfillment: Fulfillment) => {
    Modal.confirm({
      title: t('shared.orders.detail.fulfillments.complete_confirm_title'),
      content: t('shared.orders.detail.fulfillments.complete_confirm_content', { number: fulfillment.number }),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        try {
          setCompletingId(fulfillment.id)
          const fulfillmentService = await getFulfillmentService()
          const data = await (fulfillmentService as unknown as { complete: (id: string) => Promise<{ data?: { id?: string }; message?: string; interpolations?: Record<string, unknown> }> }).complete(fulfillment.id)
          if (data.data?.id) {
            message.success(t('shared.orders.detail.fulfillments.complete_success'))
            if (onReload) onReload()
          } else {
            // backend 回 message = error_code（per render_error 約定）→ FE 從 errors namespace 翻譯
            const code = data.message
            // 缺翻譯時不直接顯示 snake_case error code 給使用者，fallback 成「操作失敗 (code)」
            const fallback = code ? `${t('common.error.operation_failed')} (${code})` : t('common.error.operation_failed')
            const translated = code ? t(`errors.${code}`, { defaultValue: fallback, ...(data.interpolations || {}) }) : null
            message.error(translated || fallback)
          }
        } catch (err) {
          console.error('[FulfillmentsPanel] complete failed:', err)
          message.error(t('common.error.operation_failed'))
        } finally {
          setCompletingId(null)
        }
      },
    })
  }

  const toggleSelection = (id: string) => {
    setSelectedFulfillments(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    )
  }

  // summary 欄位數：SKU, 名稱, 庫存類別, 批號, 效期, 製造日, 自訂屬性 = 7
  const summaryColSpan = 7

  // ChangeShippingTypeModal / FulfillmentConceptModal / FulfillmentStatusLabel 仍是 .jsx，
  // TS 從其 destructure 推導出的 props 不完整（含 conditional spread / optional 欄位），
  // 因此 cast 成 ComponentType<any> 保留原本 runtime 行為。
  // 放在 component body 內以避免 Vite HMR 下的 top-level TDZ。
  const ChangeShippingTypeModalAny = ChangeShippingTypeModal as React.ComponentType<any>
  const FulfillmentConceptModalAny = FulfillmentConceptModal as React.ComponentType<any>
  const FulfillmentStatusLabelAny = FulfillmentStatusLabel as React.ComponentType<any>

  // 構建 Collapse items
  const collapseItems = fulfillments.map((fulfillment, index) => {
    const totalItems = fulfillment.items?.length || 0
    const allocatedItems = fulfillment.items?.filter(i => i.status === 'allocated' || i.status === 'non_inventoried').length || 0
    const isMerged = (fulfillment.sibling_orders && fulfillment.sibling_orders.length > 0)
    // 拆/併權限：直接信 backend 算好的 splittable / mergeable flags（避免 FE 自己重做狀態白名單造成 drift）
    // backend 規則：splittable 用 Order#splittable?、mergeable 用 Fulfillment#mergeable? + Order#mergeable?
    // FE 額外加 role === 'operator' 因為這條按鈕只給 operator 用（admin 無 split / merge UI）
    const isMergeable = role === 'operator' && fulfillment.mergeable === true
    const canSplit = role === 'operator' && fulfillment.splittable === true
    const canComplete = role === 'operator' && fulfillment.status === 'processing'
    const canChangeShippingType = ['pending', 'allocated', 'partial_allocated', 'out_of_stock'].includes(fulfillment.status || '')

    // 分組 Items
    const currentOrderItems: FulfillmentItem[] = []
    const siblingOrderItemsMap: Record<string, FulfillmentItem[]> = {} // order_id => [items]

    if (fulfillment.items) {
      fulfillment.items.forEach(item => {
        if (item.order_id === currentOrderId) {
          currentOrderItems.push(item)
        } else {
          const key = item.order_id || ''
          if (!siblingOrderItemsMap[key]) {
            siblingOrderItemsMap[key] = []
          }
          siblingOrderItemsMap[key].push(item)
        }
      })
    }

    // 獲取關聯訂單資訊 Map
    const siblingOrdersInfo: Record<string, SiblingOrder> = {}
    if (fulfillment.sibling_orders) {
      fulfillment.sibling_orders.forEach(so => {
        siblingOrdersInfo[so.id] = so
      })
    }

    // 彙整該 fulfillment 所有品項的庫存佔用
    const allInventories = (fulfillment.items || []).flatMap(item =>
      (item.inventories || []).map(inv => ({
        ...inv,
        product_sku: item.product?.sku || item.unknown_product?.sku || '-',
        product_name: item.product?.name || item.unknown_product?.name || '-',
      }))
    )

    return {
      key: fulfillment.id,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }} onClick={e => e.stopPropagation()}>
          {isMergeable && (
            <Checkbox
              checked={selectedFulfillments.includes(fulfillment.id)}
              onChange={() => toggleSelection(fulfillment.id)}
              style={{ marginRight: 12 }}
              onClick={e => e.stopPropagation()}
            />
          )}
          <Space wrap>
            <span style={{ fontWeight: 500 }}>
              {fulfillment.number || `#${index + 1}`}
            </span>
            <FulfillmentStatusLabelAny status={fulfillment.status} />
            <span style={{ color: '#888', fontSize: '12px' }}>
              ({allocatedItems}/{totalItems} {t('shared.orders.detail.fulfillments.items_allocated')})
            </span>
            {fulfillment.warehouse && (
              <Tag>{fulfillment.warehouse.name}</Tag>
            )}
            {fulfillment.shipping_type ? (
              <Tag color="geekblue">
                {t(`shipping_types.${fulfillment.shipping_type.code}`, { defaultValue: fulfillment.shipping_type.name || '-' })}
              </Tag>
            ) : null}
            <span style={{ color: '#999', fontSize: '12px' }}>
              {formatDateTime(fulfillment.created_at as string)}
            </span>
            {fulfillment.platform_tracking_number && (
              <Tag color="blue">{fulfillment.platform_tracking_number}</Tag>
            )}
          </Space>
        </div>
      ),
      extra: (canSplit || canComplete || canChangeShippingType) ? (
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {canComplete && (
            <Button
              type="text"
              size="small"
              icon={<CheckCircleOutlined />}
              loading={completingId === fulfillment.id}
              onClick={(e) => {
                e.stopPropagation()
                handleComplete(fulfillment)
              }}
              style={{ padding: '8px 12px', height: 'auto' }}
            >
              {t('shared.orders.detail.fulfillments.complete_button')}
            </Button>
          )}
          {canChangeShippingType && (
            <Button
              type="text"
              size="small"
              icon={<SwapOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                const hasBlockingShipment = (fulfillment.shipments || []).some(
                  (s) => !s.deleted_at && (s.source === 'manual_uploaded' || s.source === 'platform')
                )
                if (hasBlockingShipment) {
                  message.warning(t('shared.fulfillments.change_shipping_type.has_manual_shipment', {
                    defaultValue: '此 fulfillment 有手動/平台託運單，無法變更物流類型',
                  }))
                  return
                }
                setChangeShippingTypeTarget(fulfillment)
                setChangeShippingTypeVisible(true)
              }}
              title={t('shared.fulfillments.change_shipping_type.button', { defaultValue: '變更' })}
              style={{ padding: '8px 12px', height: 'auto' }}
            />
          )}
          {canSplit && (
            <Button
              type="text"
              size="small"
              icon={<PartitionOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                openSplitModal(fulfillment)
              }}
              title={t('shared.orders.detail.fulfillments.split.button', { defaultValue: 'Split' })}
              style={{ padding: '8px 12px', height: 'auto' }}
            />
          )}
        </div>
      ) : null,
      children: (
        <div>
          {isMerged && (
            <Alert
              message={t('shared.orders.detail.fulfillments.merged_warning')}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}

          {/* 當前訂單區塊 */}
          <Card
            title={
              <Space>
                <EnvironmentOutlined style={{ color: '#1890ff' }} />
                <span>{t('shared.orders.detail.fulfillments.current_order')}</span>
              </Space>
            }
            size="small"
            bordered={true}
            style={{ marginBottom: '16px', borderColor: '#91d5ff' }}
            headStyle={{ backgroundColor: '#e6f7ff', borderBottom: '1px solid #91d5ff' }}
          >
            <Table
              dataSource={currentOrderItems}
              columns={fulfillmentItemColumns as any}
              rowKey="id"
              pagination={false}
              size="small"
              summary={(pageData) => {
                const totalQty = (pageData as FulfillmentItem[]).reduce((sum, item) => sum + (item.quantity || 0), 0)
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={summaryColSpan}>
                        <strong style={{ fontSize: 16 }}>{t('common.total')}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={summaryColSpan} align="right">
                        {allInventories.length > 0 ? (
                          <Button type="link" size="small" style={{ padding: 0, fontSize: 28, fontWeight: 'bold' }} onClick={() => showAllInventories(allInventories)}>
                            {totalQty}
                          </Button>
                        ) : (
                          <strong style={{ fontSize: 28 }}>{totalQty}</strong>
                        )}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={summaryColSpan + 1} />
                    </Table.Summary.Row>
                  </Table.Summary>
                )
              }}
            />
          </Card>

          {/* 關聯訂單區塊 */}
          {Object.keys(siblingOrderItemsMap).length > 0 && (
            <div>
              <div style={{ margin: '16px 0', borderTop: '1px dashed #d9d9d9' }} />
              <div style={{ marginBottom: '16px', fontWeight: 'bold', color: '#666' }}>
                {t('shared.orders.detail.fulfillments.sibling_orders_title')}
              </div>

              {Object.entries(siblingOrderItemsMap).map(([orderId, items]) => {
                const orderInfo = siblingOrdersInfo[orderId] || { id: orderId, name: t('common.unknown') }
                return (
                  <Card
                    key={orderId}
                    title={
                      <Space>
                        <LinkOutlined />
                        <span>{t('models.order._')}: </span>
                        <Link to={`/orders/${orderId}`} target="_blank">
                          {orderInfo.name}
                        </Link>
                        <Tooltip title={t('common.open_in_new_tab')}>
                          <a href={`${urlPrefix}/orders/${orderId}`} target="_blank" rel="noreferrer">
                            <LinkOutlined style={{ fontSize: '12px' }} />
                          </a>
                        </Tooltip>
                      </Space>
                    }
                    extra={orderInfo.placed_at ? (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {t('models.order.placed_at')}: {formatDateTime(orderInfo.placed_at)}
                      </Text>
                    ) : null}
                    size="small"
                    type="inner"
                    style={{ marginBottom: '16px', backgroundColor: '#fafafa' }}
                  >
                    <Table
                      dataSource={items}
                      columns={fulfillmentItemColumns as any}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      summary={(pageData) => {
                        const totalQty = (pageData as FulfillmentItem[]).reduce((sum, item) => sum + (item.quantity || 0), 0)
                        return (
                          <Table.Summary fixed>
                            <Table.Summary.Row>
                              <Table.Summary.Cell index={0} colSpan={summaryColSpan}>
                                <strong style={{ fontSize: 16 }}>{t('common.total')}</strong>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={summaryColSpan} align="right">
                                <strong style={{ fontSize: 28 }}>{totalQty}</strong>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={summaryColSpan + 1} />
                            </Table.Summary.Row>
                          </Table.Summary>
                        )
                      }}
                    />
                  </Card>
                )
              })}
            </div>
          )}
        </div>
      ),
    }
  })

  return (
    <>
      {role === 'operator' && selectedFulfillments.length > 0 && (
        <div style={{ marginBottom: 16, padding: '8px', background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{t('shared.orders.merge.selected_count', { count: selectedFulfillments.length, defaultValue: `${selectedFulfillments.length} selected` })}</span>
          <Space>
            <Button size="small" onClick={() => setSelectedFulfillments([])}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="primary"
              size="small"
              icon={<MergeCellsOutlined />}
              onClick={handleMerge}
              loading={merging}
              disabled={selectedFulfillments.length < 2}
            >
              {t('shared.orders.merge.batch_merge', { defaultValue: 'Merge' })}
            </Button>
          </Space>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Tooltip title={t('concepts.fulfillments.tooltip')}>
          <Button
            type="text"
            size="small"
            icon={<QuestionCircleOutlined />}
            onClick={() => setConceptModalVisible(true)}
            style={{ color: '#8c8c8c' }}
          >
            {t('concepts.fulfillments.tooltip')}
          </Button>
        </Tooltip>
      </div>
      <Collapse
        defaultActiveKey={fulfillments.map(f => f.id)}
        items={collapseItems}
      />
      <FulfillmentConceptModalAny
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
      <FulfillmentSplitModal
        visible={splitModalVisible}
        onCancel={() => {
          setSplitModalVisible(false)
          setTargetFulfillment(null)
        }}
        onConfirm={handleSplit}
        fulfillment={targetFulfillment}
        loading={splitting}
        warehouses={merchantWarehouses}
        currentOrderId={currentOrderId}
        siblingOrders={targetFulfillment?.sibling_orders || []}
        onPreviewAmounts={async (id: string, itemsPayload: SplitItemPayload[]): Promise<PreviewResult> => {
          const fulfillmentService = await getFulfillmentService()
          // operator 端有 splitPreview，admin 暫無 — 缺則回 unsupported。
          // 同時 console.warn 讓 dev tools 可追（SplitModal 會 setPreviewFailed → 擋 Confirm，
          // 否則使用者只看到「金額預覽失敗」訊息卻不知道是 admin 不支援還是 network 失敗）。
          if (typeof (fulfillmentService as { splitPreview?: unknown }).splitPreview !== 'function') {
            console.warn('[FulfillmentsPanel] splitPreview not implemented on current role; blocking split submit')
            return { success: false, message: 'preview not supported' }
          }
          return await (fulfillmentService as unknown as { splitPreview: (id: string, items: SplitItemPayload[]) => Promise<PreviewResult> }).splitPreview(id, itemsPayload)
        }}
      />
      <ChangeShippingTypeModalAny
        visible={changeShippingTypeVisible}
        onCancel={() => {
          setChangeShippingTypeVisible(false)
          setChangeShippingTypeTarget(null)
        }}
        onSuccess={() => {
          setChangeShippingTypeVisible(false)
          setChangeShippingTypeTarget(null)
          onReload?.()
        }}
        fulfillment={changeShippingTypeTarget}
        role={role}
      />
      {renderInventoryModals()}
    </>
  )
}

export default FulfillmentsPanel
